// @ts-strict-ignore
import type { MultiSelectProps } from '@usealloy/component-library';
import { MultiSelect, useUniqueId } from '@usealloy/component-library';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { RuleBuilderContext } from '../../context';
import { assignRef } from './helpers';
import { StyledRuleMultiSelect } from '../styles';
import { isEditable } from '../../helpers';

type RuleMultiSelectProps = MultiSelectProps & {
  id?: string;
  isInvalid?: boolean;
};

const RuleMultiSelect = forwardRef((
  props: RuleMultiSelectProps,
  forwardedRef,
) => {
  const { id: propsId, isDisabled, isInvalid } = props;
  const { keyboardFocus, editMode } = useContext(RuleBuilderContext);
  const uuid = useUniqueId('rule-builder-multiselect');
  const id = propsId || uuid;

  /*
    * We cannot attach a ref directly to the focusable html element of the multiselect
    * so we have to do some ref sorcery to get it. React 18's strict mode also
    * also sometimes leads to unintended unregister calls with how it handles dependency
    * changes & callbacks. We add addtiional stabilization measures to handle that.
    */

  // Keep latest keyboardFocus in a ref to stabilize callback
  const keyboardFocusRef = useRef(keyboardFocus);

  useEffect(() => {
    keyboardFocusRef.current = keyboardFocus;
  }, [keyboardFocus]);

  // This ref will point to the focusable element (or a ref object your hook expects)
  const elementRef = useRef<HTMLElement>(null);

  // Stable callback ref: ignore null, register only when we get a real node
  const setInstance = useCallback((inst: any | null) => {
    // If MultiSelect exposes inputRef as a ref object, grab .current.
    const nextNode: HTMLElement = inst?.inputRef?.current ?? inst?.inputRef ?? null;

    if (!nextNode) return;

    if (elementRef.current !== nextNode) {
      elementRef.current = nextNode;
      assignRef(forwardedRef, nextNode);
      keyboardFocusRef.current.register({ id, ref: elementRef });
    }
  }, [id, forwardedRef]);

  useEffect(() => () => keyboardFocusRef.current.unregister(id), [id]);

  // A workaround for the lack of exposed onClick handling in Multiselect.
  // Leads to redundant setFocusedElement calls when an item is focused via a click.
  const onFocus = () => keyboardFocus.setFocusedElement(id);

  return (
    <StyledRuleMultiSelect isInvalid={isInvalid}>
      <MultiSelect
        {...props}
        onFocus={onFocus}
        id={id}
        ref={setInstance}
        isDisabled={isDisabled || !isEditable(editMode)}
      />
    </StyledRuleMultiSelect>
  );
});

RuleMultiSelect.displayName = 'RuleMultiSelect';

export default React.memo(RuleMultiSelect);
